import { useEffect, useState } from "react";
import { Button, Field, Input, Label, Textarea } from "@headlessui/react";
import { PaperClipIcon } from "@heroicons/react/24/outline";
import { useAppSelector } from "../../app/hooks";
import { handleEnterKey } from "../../app/keyboardUtils";
import { UserRole } from "../../app/userSlice";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import SimpleListbox, { SimpleOption } from "../../components/input/SimpleListbox";
import RequiredField from "../../components/RequiredField";
import {
	ContactSupportViewModel,
	SupportQuestionType,
	useContactSupportMutation,
	useGetContactSupportInfoQuery,
} from "./supportApiSlice";

const MAX_FILE_SIZE_BYTES = 10485760;

interface Props {
	onClose: () => void;
}

export default function ContactSupportForm(props: Props) {
	const { school, district, course, role } = useAppSelector((state) => state.user);

	const [model, setModel] = useState<ContactSupportViewModel>();
	const [email, setEmail] = useState("");
	const [name, setName] = useState("");
	const [questionType, setQuestionType] = useState<string | undefined>(undefined);
	const [question, setQuestion] = useState("");
	const [attachment, setAttachment] = useState<FileList | null>();
	const [attachmentString, setAttachmentString] = useState("");
	const [attachmentName, setAttachmentName] = useState("");
	const [disableSend, setDisableSend] = useState(false);
	const [sent, setSent] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	const contactSupportInfoResult = useGetContactSupportInfoQuery();
	const contactSupportInfo = contactSupportInfoResult.data;

	const [sendContactForm] = useContactSupportMutation();

	const questionTypeOptions: SimpleOption[] = [];
	Object.entries(SupportQuestionType).map(([key, value]) => {
		questionTypeOptions.push({ value: key, label: value });
	});

	useEffect(() => {
		if (model) setModel(model);
	}, [model]);

	useEffect(() => {
		if (!contactSupportInfo) return;
		setEmail(contactSupportInfo.email);
		setName(contactSupportInfo.userFullName);

		const _tempModel: ContactSupportViewModel = {
			...contactSupportInfo,
			district: contactSupportInfo.district || (district?.name ?? ""),
			school: contactSupportInfo.school || (school?.name ?? ""),
			courseName: contactSupportInfo.courseName || (course?.name ?? ""),
			courseId: contactSupportInfo.courseId || (course?.id.toString() ?? ""),
			url: window.location.href,
		};

		setModel(_tempModel);
	}, [contactSupportInfo]);

	useEffect(() => {
		if (
			!email ||
			!name ||
			!questionType ||
			!question ||
			(attachment && attachment.length > 0 && attachment[0].size > MAX_FILE_SIZE_BYTES)
		) {
			setDisableSend(true);
		} else {
			setDisableSend(false);
		}

		if (model) {
			const newModel: ContactSupportViewModel = {
				...model,
				email: email,
				userFullName: name,
				question: question,
				questionType: questionType,
				attachment: attachmentString,
				attachmentFileName: attachmentName,
			};
			setModel(newModel);
		}
	}, [email, name, question, questionType, attachmentString, attachmentName]);

	useEffect(() => {
		if (attachment && attachment.length > 0) {
			setAttachmentName(attachment[0].name);
			const reader = new FileReader();
			reader.onloadend = () => {
				if (reader.result && typeof reader.result === "string") {
					const base64String = reader.result.split(",")[1];
					setAttachmentString(base64String);
				}
			};
			reader.readAsDataURL(attachment[0]);

			if (attachment && attachment[0].size > MAX_FILE_SIZE_BYTES) {
				setErrorMessage("File too large. File size cannot be larger than 10 MB.");
			} else {
				setErrorMessage("");
			}
		} else {
			setAttachmentName("");
			setErrorMessage("");
		}
	}, [attachment]);

	function handleSend() {
		if (model && role != UserRole.Writer) {
			sendContactForm(model)
				.unwrap()
				.then(() => {
					setErrorMessage("");
					setSent(true);
				})
				.catch((error) => {
					setErrorMessage(error.data);
				});
		}
	}

	function handleClose() {
		setErrorMessage("");
		props.onClose();
	}

	return (
		<div>
			<AlertMessage
				className="mt-2 text-sm"
				type={AlertLevel.Error}
				message={errorMessage}
				showIcon={true}
			/>
			{sent ? (
				<div>
					<p className="mt-4 text-center">
						Thank you for your submission. Know that it is being reviewed by our support
						team and someone will be responding to you soon.
					</p>
					<Button
						id="close-button"
						onClick={handleClose}
						onKeyDown={(e) => handleEnterKey(e, handleClose)}
						className="btn btn-neutral mt-4 w-full">
						Close
					</Button>
				</div>
			) : (
				<div>
					{contactSupportInfo && (
						<div>
							<Field className="mt-2">
								<div className="flex justify-between gap-4 mt-2">
									<Label htmlFor="email" className="font-semibold text-sm">
										Email <RequiredField />
									</Label>
									<p className="text-xs text-gray-600">
										<RequiredField className="text-base" /> - Denotes a required
										field
									</p>
								</div>
								<Input
									id="email"
									name="email"
									type="email"
									value={email}
									readOnly={contactSupportInfo.userHasEmail}
									className="block w-full mt-1 rounded-lg border-none text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									onChange={(e) => setEmail(e.target.value)}
								/>
							</Field>
							<div className="grid grid-cols-2 gap-x-3 mt-2">
								<Field>
									<Label htmlFor="name" className="font-semibold text-sm">
										Full Name <RequiredField />
									</Label>
									<Input
										id="name"
										name="name"
										type="text"
										value={name}
										className="block w-full mt-1 rounded-lg border-none text-gray-900 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										onChange={(e) => setName(e.target.value)}
									/>
								</Field>
								<Field>
									<Label htmlFor="username" className="font-semibold text-sm">
										Username <RequiredField />
									</Label>
									<Input
										id="username"
										name="username"
										type="text"
										value={contactSupportInfo.userName}
										readOnly={true}
										className="block w-full mt-1 rounded-lg border-none text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									/>
								</Field>
							</div>
							<Field className="mt-2">
								<Label htmlFor="questionType" className="font-semibold text-sm">
									Type of Question <RequiredField />
								</Label>
								<SimpleListbox
									id="questionType"
									options={questionTypeOptions}
									prompt="Please select from the following list..."
									value={questionType}
									className="w-full mt-1 text-gray-900 focus:ring-1 focus:ring-inset focus:ring-primary-600 !py-1.5"
									onChange={(e) => setQuestionType(e.target.value)}
								/>
							</Field>
							<Field className="mt-2">
								<Label htmlFor="question" className="font-semibold text-sm">
									Question <RequiredField />
								</Label>
								<Textarea
									id="question"
									name="question"
									placeholder="Please include any relevant information..."
									value={question}
									className="block w-full mt-1 h-32 resize-none rounded-lg border-none text-gray-900 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									onChange={(e) => setQuestion(e.target.value)}
								/>
							</Field>
							<Field className="mt-2">
								<Label htmlFor="attachment" className="font-semibold text-sm">
									Attachment
								</Label>
								<div className="flex h-11 w-full mt-1">
									<Input
										id="attachment"
										name="attachment"
										type="text"
										placeholder="No file chosen"
										value={attachmentName}
										className="block rounded-none grow rounded-l-md border-0 p-2 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										disabled={true}
									/>
									<button className="btn btn-neutral !rounded-l-none w-36 ">
										<label className="flex hover:cursor-pointer">
											<PaperClipIcon className="size-5" aria-hidden="true" />
											<span className="flex pl-2 items-center">
												Choose File
											</span>
											<Input
												id="attachment"
												name="attachment"
												type="file"
												multiple={false}
												accept="image/*, .doc, .docx, .pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/msword"
												className="sr-only"
												onChange={(e) => setAttachment(e.target.files)}
											/>
										</label>
									</button>
								</div>
							</Field>
						</div>
					)}
					<div className="mt-8 grid grid-cols-2 gap-3">
						<Button
							id="cancel-button"
							onClick={handleClose}
							onKeyDown={(e) => handleEnterKey(e, handleClose)}
							className="btn btn-neutral">
							Cancel
						</Button>
						<Button
							id="send-button"
							disabled={disableSend}
							onClick={handleSend}
							onKeyDown={(e) => handleEnterKey(e, handleSend)}
							className="btn btn-primary">
							Send
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}
